import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
export const fmt2 = (v) => new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(v || 0);
export const LIBELLES_STATUT_FACTURE = { emise: 'Émise', remplacee: 'Remplacée', annulee: 'Annulée' };
const CLASSES = { emise: 'bg-emerald-100 text-emerald-800', remplacee: 'bg-amber-100 text-amber-800', annulee: 'bg-slate-200 text-slate-600 line-through' };

// Téléchargement authentifié (le Bearer est injecté par l'intercepteur axios ; un <a href> nu ne le porterait pas).
export async function telechargerBlob(url, nomFichier, ouvrir) {
  const r = await axios.get(url, { responseType: 'blob' });
  const href = URL.createObjectURL(r.data);
  if (ouvrir) { window.open(href, '_blank'); return; }
  const a = document.createElement('a'); a.href = href; a.download = nomFichier; a.click();
  setTimeout(() => URL.revokeObjectURL(href), 10000);
}

export const BadgeFacture = ({ f }) => (
  <span className={`text-xs px-2 py-0.5 rounded-full ${CLASSES[f.statut]}`} data-testid={`facture-statut-${f.id}`}>
    {LIBELLES_STATUT_FACTURE[f.statut]}{f.version > 0 ? ` · v${f.version}` : ''}{f.mois_depaye ? ' · mois dé-payé' : ''}
  </span>
);

// 🧾 Phase 6 : aperçu fidèle au PDF (mêmes blocs, mêmes montants — instantané de la facture).
export function ApercuFacture({ f }) {
  if (!f) return null;
  const fo = f.fournisseur || {}, cl = f.client || {};
  return (
    <div className="text-sm space-y-3" data-testid="apercu-facture">
      <div className="flex justify-between gap-4">
        <div><p className="text-xl font-bold tracking-widest">{f.titre || 'FACTURE'}</p><p>N° <b data-testid="apercu-numero">{f.numero}</b></p><p>Date : {f.date_facture}</p>
          <p>Période : semaine {f.semaine} ({f.annee}) — du {f.periode?.debut} au {f.periode?.fin}</p></div>
        <div className="border rounded p-2 w-1/2 text-xs" data-testid="apercu-fournisseur"><b className="block">{fo.nom_legal || fo.nom}</b>{fo.adresse_facturation}
          {fo.neq && <div>NEQ {fo.neq}</div>}{fo.no_tps && <div>TPS {fo.no_tps}</div>}{fo.no_tvq && <div>TVQ {fo.no_tvq}</div>}</div>
      </div>
      {f.statut === 'remplacee' && <p className="border-2 border-red-700 text-red-700 font-bold text-center p-1">REMPLACÉE par {f.remplacee_par_numero || 'une version corrigée'}</p>}
      <div className="border rounded p-2 text-xs w-2/3" data-testid="apercu-client"><b className="block">Facturé à : {cl.nom}</b>{cl.adresse}</div>
      <table className="w-full text-xs"><thead><tr className="bg-slate-100 text-left"><th className="p-1">Description des services</th><th className="p-1 text-right">Contrat</th><th className="p-1 text-right">{f.libelle_part}</th></tr></thead>
        <tbody>{(f.lignes || []).map((l, i) => <tr key={i} className="border-b"><td className="p-1">{l.description}</td><td className="p-1 text-right">{l.montant_contrat ? fmt2(l.montant_contrat) : ''}</td><td className="p-1 text-right">{fmt2(l.montant)}</td></tr>)}</tbody></table>
      <table className="text-xs ml-auto w-1/2"><tbody>
        <tr><td>Sous-total (avant taxes)</td><td className="text-right">{fmt2(f.sous_total)}</td></tr>
        {f.deductions_manuelles > 0 && <tr><td>Déductions manuelles du rapport</td><td className="text-right">− {fmt2(f.deductions_manuelles)}</td></tr>}
        {(f.deductions_auto || []).map((d, i) => <tr key={i} data-testid="apercu-deduction-auto"><td>{d.description}{d.origine ? ` — ${d.origine}` : ''}</td><td className="text-right">− {fmt2(d.montant)}</td></tr>)}
        <tr><td>Montant net avant taxes</td><td className="text-right">{fmt2(f.montant_net_avant_taxes)}</td></tr>
        <tr><td>TPS (5 %)</td><td className="text-right">{fmt2(f.tps)}</td></tr><tr><td>TVQ (9,975 %)</td><td className="text-right">{fmt2(f.tvq)}</td></tr>
        <tr className="font-bold border-t-2 border-slate-800"><td>MONTANT FINAL À PAYER</td><td className="text-right" data-testid="apercu-montant-final">{fmt2(f.montant_final)}</td></tr>
      </tbody></table>
      <p className="text-xs text-slate-500 border-t pt-1">{f.mention_pied}</p>
    </div>
  );
}

export function DialogFacture({ installateurId, factureId, onFermer }) {
  const [f, setF] = useState(null);
  useEffect(() => {
    if (!factureId) return;
    setF(null);
    axios.get(`${API}/api/lmds/installateurs/${installateurId}/factures/${factureId}`).then((r) => setF(r.data)).catch(() => setF({ erreur: true }));
  }, [installateurId, factureId]);
  const pdfUrl = `${API}/api/lmds/installateurs/${installateurId}/factures/${factureId}/pdf`;
  const ouvrir = () => telechargerBlob(pdfUrl, `${f.numero}.pdf`, true);
  const telecharger = () => telechargerBlob(pdfUrl, `${f.numero}.pdf`, false);
  return (
    <Dialog open={!!factureId} onOpenChange={(o) => !o && onFermer()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto" data-testid="dialog-facture">
        <DialogHeader><DialogTitle>Facture {f?.numero || ''}</DialogTitle></DialogHeader>
        {f?.erreur ? <p className="text-red-600 text-sm">Impossible de charger la facture.</p> : <ApercuFacture f={f} />}
        {f && !f.erreur && (
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={ouvrir} data-testid="facture-imprimer">Imprimer</Button>
            <Button onClick={telecharger} data-testid="facture-telecharger">Télécharger PDF</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
